import {
  transformActionFilters,
  transformFollowedFiltersToArray,
  transformReadFiltersToArray,
  transformSharedFiltersToArray,
} from '@/util/action-filters-util';
import { i18n } from '@/plugins/i18n';
import { expandToZones, mapToZones } from '@/util/zones-util';
import { expandSeaoRegions, contractSeaoRegions } from '@/models/search-filters';

/**
 * A "tender view" is a named, saved set of tenders filters that the user can
 * switch between (replacing the old fixed mode navigation). A view can be
 * private to its owner or shared across the tenant.
 *
 * The `filters` blob mirrors the shape persisted by user-profile.service.js
 * (the same object stored today as the single `searchFilters` set), so a view
 * is essentially "a named searchFilters set + some metadata".
 */

export const VIEW_SCOPE = {
  PRIVATE: 'PRIVATE',
  SHARED: 'SHARED',
};

// Hard cap enforced by the DB on a view name. Kept here so the input limit and the duplicate-suffix
// truncation stay in sync with it.
export const VIEW_NAME_MAX_LENGTH = 120;

/**
 * The persisted filter keys that define a view. Transient filters (the free-text
 * query and publication dates — see TRANSIENT_FILTER_KEYS) are intentionally
 * excluded: they are per-session, not part of a saved view, and must not make a
 * view look "dirty".
 */
export const VIEW_FILTER_KEYS = [
  'platforms',
  'statuses',
  'closingBefore',
  'keywordIds',
  'matchContexts',
  'procurementCategories',
  'zones',
  'readFilters',
  'shareFilters',
  'followFilters',
  'assigneesFilters',
  'administrativeRegions',
  'classifications',
  // New fields backed by the real API:
  // allKeywords: true = match ALL of the tenant's current keywords dynamically (ignore keywordIds)
  // archived: true = show ONLY archived tenders; null/false = normal (exclude archived)
  'allKeywords',
  'archived',
  // relevanceScoreRange = array of {start,end} bands (keyword-relevance score filter).
  'relevanceScoreRange',
  // includeUnlocated: opt-in, zone filters also include tenders whose location is unknown.
  'includeUnlocated',
];

/**
 * Transient filter keys applied to the CURRENT search only — never persisted on a
 * saved view and never counted toward "is this view dirty?". The free-text query and
 * publication dates are per-session overlays (see the "dates temporaires" note in the
 * date picker), not part of a saved view.
 */
export const TRANSIENT_FILTER_KEYS = ['query', 'publishedAfter', 'publishedBefore'];

export const ARRAY_FILTER_KEYS = [
  'platforms',
  'statuses',
  'keywordIds',
  'matchContexts',
  'procurementCategories',
  'readFilters',
  'shareFilters',
  'followFilters',
  'assigneesFilters',
  'classifications',
];

export function emptyViewFilters() {
  return {
    platforms: [],
    statuses: [],
    closingBefore: null,
    keywordIds: [],
    matchContexts: [],
    procurementCategories: [],
    zones: {},
    readFilters: [],
    shareFilters: [],
    followFilters: [],
    assigneesFilters: [],
    administrativeRegions: {},
    classifications: [],
    allKeywords: false,
    archived: null,
    query: '',
    publishedAfter: null,
    publishedBefore: null,
    relevanceScoreRange: [],
    includeUnlocated: false,
  };
}

/** Deep-ish clone of just the persisted view-filter keys. */
export function cloneFilters(filters = {}) {
  const base = emptyViewFilters();
  const out = {};
  VIEW_FILTER_KEYS.forEach((key) => {
    const value = filters[key] !== undefined ? filters[key] : base[key];
    if (Array.isArray(value)) {
      out[key] = [...value];
    } else if (value && typeof value === 'object') {
      out[key] = JSON.parse(JSON.stringify(value));
    } else {
      out[key] = value;
    }
  });
  return out;
}

function normalizeForCompare(filters = {}) {
  const base = emptyViewFilters();
  const normalized = {};
  VIEW_FILTER_KEYS.forEach((key) => {
    let value = filters[key];
    if (value === undefined || value === null) {
      value = base[key];
    }
    if (ARRAY_FILTER_KEYS.includes(key) && Array.isArray(value)) {
      // Sort by a stable string key so object-valued arrays (e.g. classifications) normalize
      // order-independently — a bare Array.sort() stringifies objects to "[object Object]".
      const sortKey = (v) => (v !== null && typeof v === 'object' ? JSON.stringify(v) : String(v));
      value = [...value].sort((x, y) => {
        const kx = sortKey(x);
        const ky = sortKey(y);
        return kx < ky ? -1 : kx > ky ? 1 : 0;
      });
    }
    normalized[key] = value;
  });
  return JSON.stringify(normalized);
}

/** True when two filter sets are equivalent for the purposes of "is this view dirty?". */
export function areFiltersEqual(a, b) {
  return normalizeForCompare(a) === normalizeForCompare(b);
}

/** A tenant-admin-curated preset view (backend `preset: true`). */
export function isPresetView(view) {
  return !!(view && view.preset);
}

/**
 * True when filters restrict results to the user's followed items (the
 * watchlist) — either the boolean flag or a `states` entry carried by the
 * API's followed view.
 */
export function isWatchlistFilters(filters = {}) {
  return (
    filters.followed === true ||
    (Array.isArray(filters.states) && filters.states.some((state) => String(state).toUpperCase().includes('FOLLOW')))
  );
}

/** The "Shared" view: only what is shared with the tenant (`shared`, or a `SHARED` state). */
export function isSharedViewFilters(filters = {}) {
  return (
    filters.shared === true ||
    (Array.isArray(filters.states) && filters.states.some((state) => String(state).toUpperCase() === 'SHARED'))
  );
}

/**
 * Display label for a view. Seeded default views (Saved/Shared/Archived) carry an i18n
 * `nameKey` and only an English fallback in `name`, so translate the key when present;
 * user-created views have `nameKey: null` and render their typed `name`.
 * @param {object} view
 * @param {(key: string) => string} t - the component's `$t`
 */
export function viewLabel(view) {
  if (!view) return '';
  // Seeded defaults carry an i18n nameKey; user views just use their stored name.
  return view.nameKey ? i18n.t(view.nameKey) : view.name;
}

function normalizeViewLabel(value) {
  return (value || '')
    .toString()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();
}

/**
 * Seeded default view kind (saved / shared / archived), or null for
 * user-created views and other admin presets. Heuristics run only when
 * `view.preset` is set, so a user view named like a default or carrying
 * followed/shared/archived filters is not treated as a locked seed.
 */
function viewKind(view) {
  if (!isPresetView(view)) return null;

  // Match the final key segment exactly: a substring test would also catch
  // negated siblings like `...unsaved` / `...unshared`.
  const leaf = (view.nameKey || '').toLowerCase().split('.').pop();
  if (leaf === 'followed' || leaf === 'saved') return 'saved';
  if (leaf === 'shared') return 'shared';
  if (leaf === 'archived') return 'archived';

  const name = normalizeViewLabel(view.name);
  const matches = (label) => name && name === normalizeViewLabel(label);
  const translate = (i18nKey) => {
    try {
      return i18n && i18n.t ? i18n.t(i18nKey) : '';
    } catch (e) {
      return '';
    }
  };
  if (matches(translate('views.default.followed')) || matches(translate('views.default.saved')) || name === 'saved') {
    return 'saved';
  }
  if (matches(translate('views.default.shared')) || name === 'shared') return 'shared';
  if (matches(translate('views.default.archived')) || name === 'archived') return 'archived';

  const filters = view.filters || {};
  if (isWatchlistFilters(filters)) return 'saved';
  if (filters.shared === true) return 'shared';
  if (filters.archived === true) return 'archived';

  return null;
}

const SEEDED_VIEW_ICONS = {
  saved: 'bookmark',
  shared: 'share-2',
  archived: 'archive',
};

/** Tab/list icon for a view: seeded defaults get a distinctive mark, presets stay locked. */
export function viewIconName(view) {
  const kind = viewKind(view);
  if (kind && SEEDED_VIEW_ICONS[kind]) return SEEDED_VIEW_ICONS[kind];
  if (view && view.preset) return 'lock';
  return view && view.scope === VIEW_SCOPE.SHARED ? 'users' : 'user';
}

/**
 * DTO (view.filters, as returned by the API) -> working shape used by the filter editor
 * (search-filters module). Only read/shared/followed booleans -> arrays and
 * assignees -> assigneesFilters are remapped; everything else is verbatim.
 */
export function viewFiltersToWorking(filters = {}) {
  const f = filters || {};
  return {
    platforms: f.platforms || [],
    statuses: f.statuses || [],
    closingBefore: f.closingBefore || null,
    keywordIds: f.keywordIds || [],
    matchContexts: f.matchContexts || [],
    procurementCategories: f.procurementCategories || [],
    // Contract the stored display-name zones back to ZoneTree's enum keys for the editor.
    zones: mapToZones(f.zones || {}),
    // Same for SEAO regions: stored as display values, edited as enum keys.
    administrativeRegions: contractSeaoRegions(f.administrativeRegions || {}),
    classifications: f.classifications || [],
    readFilters: transformReadFiltersToArray(f.read === undefined ? null : f.read),
    shareFilters: transformSharedFiltersToArray(f.shared === undefined ? null : f.shared),
    followFilters: transformFollowedFiltersToArray(f.followed === undefined ? null : f.followed),
    assigneesFilters: f.assignees || [],
    allKeywords: !!f.allKeywords,
    archived: f.archived === undefined ? null : f.archived,
    query: f.query || '',
    publishedAfter: f.publishedAfter || null,
    publishedBefore: f.publishedBefore || null,
    relevanceScoreRange: f.relevanceScoreRange || [],
    includeUnlocated: !!f.includeUnlocated,
  };
}

/**
 * Working shape -> DTO (view.filters) for POST /tenders/search {filters} and for
 * saving a view. Inverse of viewFiltersToWorking. No closingBefore date conversion or
 * keyword resolution — the backend does that. Zones ARE expanded to their display-name
 * form here (e.g. { CANADA: { QUEBEC: [] } } -> { Canada: { Quebec: [], Québec: [] } }),
 * because the search endpoint matches on names, not the ZoneTree enum keys.
 */
export function workingToViewFilters(working = {}) {
  const w = working || {};
  const action = transformActionFilters([
    ...(w.readFilters || []),
    ...(w.shareFilters || []),
    ...(w.followFilters || []),
  ]);
  const orNull = (v) => (v === undefined ? null : v);
  return {
    platforms: w.platforms || [],
    statuses: w.statuses || [],
    closingBefore: w.closingBefore || null,
    procurementCategories: w.procurementCategories || [],
    zones: expandToZones(w.zones || {}),
    keywordIds: w.keywordIds || [],
    matchContexts: w.matchContexts || [],
    read: orNull(action.read),
    shared: orNull(action.shared),
    followed: orNull(action.followed),
    assignees: w.assigneesFilters || [],
    // SEAO regions: expand enum keys -> display values (the search endpoint matches on names).
    administrativeRegions: expandSeaoRegions(w.administrativeRegions || {}),
    classifications: w.classifications || [],
    allKeywords: !!w.allKeywords,
    archived: w.archived === undefined ? null : w.archived,
    query: w.query || null,
    publishedAfter: w.publishedAfter || null,
    publishedBefore: w.publishedBefore || null,
    relevanceScoreRange: w.relevanceScoreRange || [],
    includeUnlocated: !!w.includeUnlocated,
  };
}

/**
 * Strip transient (per-session) keys from a DTO before persisting a view, so
 * publication dates ride along in searches but are never saved on the backend.
 */
export function stripTransientFilters(filters = {}) {
  const out = { ...filters };
  TRANSIENT_FILTER_KEYS.forEach((key) => {
    out[key] = null;
  });
  return out;
}
